import type { FormId } from '@risksmart-app/shared/forms/formConfigRegistry';
import type { FormFieldConfig } from '@risksmart-app/shared/forms/types';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  FieldTypeOption,
  FormEditorInitialData,
} from 'src/blocks/form-editor-dialog';

import { useIsModuleEnabledLazy } from '@/hooks/useIsModuleEnabled';
import { useFormConfigRegistry } from '@/utils/table/hooks/form/useFormConfigRegistry';
import { getCustomAttributeRenderProps } from '@/utils/table/utils/customAttributes';

import { fieldTypesConfig } from '../custom-attributes/field-types';
import {
  convertConditionsToEditorRules,
  defaultFieldConditionToConditions,
} from './convertEditorStateToFormData';
import { useRiskSmartForm } from './customisable-form/RiskSmartFormContext';
import { useCustomisableFormDataContext } from './customisable-form-data/CustomisableFormDataContext';
import { useFormFieldElementsStore } from './stores/useFormFieldElementsStore';
import { useFormFieldRegistryStore } from './stores/useFormFieldRegistryStore';

const RISK_RATING_FORM_IDS: FormId[] = [
  'controlled_risk_assessment_result',
  'uncontrolled_risk_assessment_result',
  'risk_controlled_internal_audit_result',
  'risk_uncontrolled_internal_audit_result',
  'risk_controlled_second_line_result',
  'risk_uncontrolled_second_line_result',
];

const GROUP_DISPLAY_NAMES: Record<string, string> = {
  testConfigFields: 'Test configuration',
};

const getGroupDisplayName = (groupFieldId: string): string =>
  GROUP_DISPLAY_NAMES[groupFieldId] ??
  groupFieldId.replace(/([A-Z])/g, ' $1').trim();

const getGroupSubtitle = (groupFieldId: string): string =>
  groupFieldId === 'testConfigFields' ? 'Test schedule configuration' : '';

/**
 * Maps a custom attribute type to the form editor's field type.
 * Since the editor now uses CustomAttributeFieldType values directly,
 * this is a pass-through for all known types.
 */
const mapCustomAttributeType = (type: string): string => type;

const mapDisplayTypeToFieldType = (
  registryField: FormFieldConfig | undefined
): string => {
  if (!registryField) {
    return '';
  }
  if (!registryField.displayType) {
    return 'text';
  }

  const { displayType } = registryField.displayType;

  switch (displayType) {
    case 'commonLookup':
    case 'rating':
      return 'dropdown';
    case 'users':
      return 'multiselect' in registryField.displayType &&
        registryField.displayType.multiselect === false
        ? 'dropdown'
        : 'usermultiselect';
    case 'departments':
      return 'departmentmultiselect';
    case 'tags':
    case 'badgeList':
      return 'multiselect' in registryField.displayType &&
        registryField.displayType.multiselect === false
        ? 'dropdown'
        : 'multiselect';
    case 'textarea':
      return 'textarea';
    case 'date':
      return 'date';
    case 'number':
      return 'number';
    default:
      return '';
  }
};

interface UseFormEditorInitialDataResult {
  initialData: FormEditorInitialData;
  fieldTypeOptions: FieldTypeOption[];
}

/**
 * Builds the initialData and fieldTypeOptions needed by the FormEditorDialog.
 *
 * Extracts the data transformation logic that was previously inline in FormMenu,
 * reading from the form data context, field registry, and form config registry.
 */
export const useFormEditorInitialData = (
  parentType: FormId
): UseFormEditorInitialDataResult => {
  const { forcedRequiredFields, defaultRequiredFields } = useRiskSmartForm();
  const {
    formSections,
    formFieldConfigurations,
    formFieldOrdering,
    customAttributeSchema,
  } = useCustomisableFormDataContext();
  const registeredFields = useFormFieldRegistryStore(
    (state) => state.getAllFields
  );
  const currentFormElements = useFormFieldElementsStore(
    (state) => state.elements
  );
  const defaultSections = useFormFieldElementsStore(
    (state) => state.defaultSections
  );
  const formConfigRegistry = useFormConfigRegistry();
  const formConfig = formConfigRegistry[parentType];
  const { t } = useTranslation(['common']);
  const isModuleEnabled = useIsModuleEnabledLazy();

  const initialData = useMemo((): FormEditorInitialData => {
    const sortedSections = [...(formSections ?? [])].sort(
      (a, b) => (a.ordering?.Position ?? 0) - (b.ordering?.Position ?? 0)
    );

    // Inverse of the save-time translation: form-value key → editor id.
    // Custom fields go from server format back to scope format; standard
    // fields go from canonical casing back to the React key the form uses.
    // We can't assume the React key is the canonical FieldId lowercased —
    // many forms use camelCase keys (e.g. `policyBreach`) — so prefer the
    // actual registered key (matched case-insensitively on FieldId); fall
    // back to canonical-lowercase only when the field isn't registered.
    const formValueKeyToEditorId = (formValueKey: string): string => {
      if (formValueKey.startsWith('CustomAttributeData.')) {
        return `#/properties/${formValueKey.slice('CustomAttributeData.'.length)}`;
      }
      const lowerKey = formValueKey.toLowerCase();
      const registered = registeredFields().find(
        (r) => r.fieldId.toLowerCase() === lowerKey
      );
      if (registered) {
        return registered.fieldId;
      }
      const standard = formConfig
        ? Object.values(formConfig).find((f) => f.fieldId === formValueKey)
        : undefined;

      return standard ? standard.fieldId.toLowerCase() : formValueKey;
    };
    const remapRules = (
      rules: { ifField: string; values: string[] }[]
    ): { ifField: string; values: string[] }[] =>
      rules.map((r) => ({ ...r, ifField: formValueKeyToEditorId(r.ifField) }));

    const orderByPosition = (fieldIds: string[]) =>
      [...fieldIds].sort((a, b) => {
        const aPos =
          formFieldOrdering?.find((o) => o.FieldId === a)?.Position ?? 0;
        const bPos =
          formFieldOrdering?.find((o) => o.FieldId === b)?.Position ?? 0;

        return aPos - bPos;
      });

    // Mirror useSectionManager's precedence: customer-defined DB sections win;
    // otherwise fall back to the form's hardcoded `<DefaultSection>` defaults;
    // otherwise a single "Details" section holding every field. Uses the
    // elements store (now scoped per form instance via CustomisableFormDataProvider)
    // rather than the global field registry to avoid including fields from other
    // mounted forms.
    const allFieldIds = currentFormElements.map((e) => e.key);
    const buildDefaultSections = () => {
      if (defaultSections.length === 0) {
        return [
          {
            id: 'section_details',
            name: 'Details',
            fieldIds: orderByPosition(allFieldIds),
          },
        ];
      }

      const builtSections = defaultSections.map((s) => ({
        id: s.SectionId,
        name: s.Label,
        fieldIds: orderByPosition(s.fields),
      }));

      // Park fields not mapped to any default section (e.g. runtime-injected
      // custom attributes) at the end of the last section, matching
      // useSectionManager.
      const mappedFieldIds = new Set(builtSections.flatMap((s) => s.fieldIds));
      const unmappedFieldIds = allFieldIds.filter(
        (fieldId) => !mappedFieldIds.has(fieldId)
      );
      if (unmappedFieldIds.length > 0) {
        builtSections[builtSections.length - 1].fieldIds.push(
          ...unmappedFieldIds
        );
      }

      return builtSections;
    };

    const sections =
      sortedSections.length === 0
        ? buildDefaultSections()
        : sortedSections.map((s) => {
            const sectionConditions = convertConditionsToEditorRules(
              s.Conditions
            );

            return {
              id: s.SectionId,
              name: s.Label,
              fieldIds: orderByPosition((s.fields ?? []).map((f) => f.FieldId)),
              ...(sectionConditions.enabled
                ? {
                    config: {
                      name: s.Label,
                      description: s.Description ?? '',
                      conditionalLogicEnabled: true,
                      conditionalLogicRules: remapRules(
                        sectionConditions.rules
                      ),
                      guidanceEnabled: false,
                      guidance: '',
                    },
                  }
                : {}),
            };
          });

    // Build a lookup from custom attribute schema for custom field labels/types
    const customFieldRenderProps = customAttributeSchema
      ? getCustomAttributeRenderProps(
          customAttributeSchema.Schema,
          customAttributeSchema.UiSchema
        )
      : [];
    const customFieldByScope = new Map(
      customFieldRenderProps.map((rp) => [rp.scope, rp])
    );

    const isRiskRatingForm = RISK_RATING_FORM_IDS.includes(parentType);
    const fields: FormEditorInitialData['fields'] = {};
    for (const reg of registeredFields()) {
      const config = formFieldConfigurations?.find(
        (fc) => fc.FieldId === reg.fieldId
      );
      const registryField = formConfig
        ? Object.values(formConfig).find(
            (f) => f.fieldId.toLowerCase() === reg.fieldId.toLowerCase()
          )
        : undefined;

      const isGroup = !reg.isCustom && !registryField;
      const customRenderProp = reg.isCustom
        ? customFieldByScope.get(reg.fieldId)
        : undefined;

      const fieldIdLower = reg.fieldId.toLowerCase();
      const isForcedRequired = forcedRequiredFields.some(
        (f) => f.toLowerCase() === fieldIdLower
      );
      const isDefaultRequired = defaultRequiredFields.some(
        (f) => f.toLowerCase() === fieldIdLower
      );

      // Resolve display name: custom field label from schema, config override,
      // static registry label, group name, or fallback to fieldId
      const resolvedName = isGroup
        ? getGroupDisplayName(reg.fieldId)
        : (config?.Label ??
          customRenderProp?.label ??
          registryField?.formLabel ??
          reg.fieldId);

      // Default/original label — what the field would render with no override.
      // Used by the field editor as a read-only reference when the user has
      // applied a custom label (i.e. resolvedName differs from this).
      const originalLabel = isGroup
        ? undefined
        : (registryField?.formLabel ?? customRenderProp?.label);

      // Map custom attribute options to the editor's { id, label } format
      const editorOptions =
        customRenderProp?.options?.map((opt) => ({
          id:
            opt._tag === 'AltValueOption'
              ? (opt as { AltValue: string }).AltValue
              : opt.Value,
          label: opt.Value,
        })) ?? [];

      // Seed conditional logic from a code-declared default when no persisted
      // condition exists (mirrors the section-default seeding). A read-only
      // default always wins for display and is locked; an editable default is
      // shown pre-filled and only persists once the admin edits it.
      const defaultCondition = registryField?.defaultConditions;
      // Mirror getEffectiveFieldConditions' definition of "has a persisted
      // override" (non-empty tokenGroups) so editor seeding and runtime never
      // disagree on whether a default applies.
      const persistedConditions =
        config?.Conditions && config.Conditions.tokenGroups.length > 0
          ? config.Conditions
          : null;
      const effectiveConditions = defaultCondition?.readOnly
        ? defaultFieldConditionToConditions(defaultCondition)
        : (persistedConditions ??
          (defaultCondition
            ? defaultFieldConditionToConditions(defaultCondition)
            : null));
      const fieldConditions =
        convertConditionsToEditorRules(effectiveConditions);
      const conditionalLogicLocked =
        !!defaultCondition?.readOnly && fieldConditions.enabled;
      const conditionalLogicIsDefault =
        !defaultCondition?.readOnly &&
        !persistedConditions &&
        !!defaultCondition &&
        fieldConditions.enabled;
      const fieldType = customRenderProp
        ? mapCustomAttributeType(customRenderProp.type)
        : mapDisplayTypeToFieldType(registryField);

      fields[reg.fieldId] = {
        name: resolvedName,
        type: fieldType,
        required: config?.Required ?? (isForcedRequired || isDefaultRequired),
        readOnly: config?.ReadOnly ?? false,
        isCustom: reg.isCustom,
        forcedRequired: isForcedRequired,
        canDelete: reg.isCustom,
        canEditLabel:
          reg.isCustom ||
          (!isRiskRatingForm && !reg.fieldId.includes('schedule.')),
        canEditOptions: !isForcedRequired,
        subtitle: isGroup ? getGroupSubtitle(reg.fieldId) : undefined,
        isGroup,
        originalLabel,
        ...(editorOptions.length > 0 || fieldConditions.enabled
          ? {
              config: {
                fieldType,
                fieldName: resolvedName,
                required:
                  config?.Required ?? (isForcedRequired || isDefaultRequired),
                readOnly: config?.ReadOnly ?? false,
                hidden: config?.Hidden ?? false,
                defaultValueEnabled: false,
                defaultValue: '',
                options: editorOptions,
                conditionalLogicEnabled: fieldConditions.enabled,
                conditionalLogicRules: remapRules(fieldConditions.rules),
                conditionalLogicLocked,
                conditionalLogicIsDefault,
                guidanceEnabled: false,
                guidance: '',
              },
            }
          : {}),
      };
    }

    // Append custom attribute fields not already mapped to a section,
    // matching useSectionManager behaviour for unmapped custom fields
    if (customAttributeSchema && sections.length > 0) {
      const mappedFieldIds = new Set(sections.flatMap((s) => s.fieldIds));
      const renderProps = getCustomAttributeRenderProps(
        customAttributeSchema.Schema,
        customAttributeSchema.UiSchema
      );
      const unmappedCustomFieldIds: string[] = [];
      for (const rp of renderProps) {
        if (!mappedFieldIds.has(rp.scope) && fields[rp.scope]) {
          unmappedCustomFieldIds.push(rp.scope);
        }
      }
      if (unmappedCustomFieldIds.length > 0) {
        sections[sections.length - 1].fieldIds.push(...unmappedCustomFieldIds);
      }
    }

    return { sections, fields };
  }, [
    formSections,
    formFieldConfigurations,
    formFieldOrdering,
    registeredFields,
    currentFormElements,
    defaultSections,
    formConfig,
    parentType,
    forcedRequiredFields,
    defaultRequiredFields,
    customAttributeSchema,
  ]);

  const fieldTypeOptions = useMemo(
    (): FieldTypeOption[] =>
      Object.entries(fieldTypesConfig)
        .filter(
          ([, config]) =>
            !config.hasPermission || config.hasPermission(isModuleEnabled)
        )
        .map(([type, config]) => ({
          value: type,
          label: config.i18nKey ? (t(config.i18nKey) as string) : type,
        })),
    [isModuleEnabled, t]
  );

  return { initialData, fieldTypeOptions };
};
